/**
 * VendorPortalActivityService
 * Service for viewing vendor activity logs and status history (read-only).
 * Route: api/vendor-portal/activity
 *
 * Vendors can:
 * - View their activity logs
 * - View status change history
 * - View combined timeline of logs and status changes
 * - Get activity summary statistics
 *
 * READ-ONLY: Vendors cannot create or modify activity logs.
 * Activity logs are automatically created by the system.
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalActivityService
 * Handles vendor activity log and status history viewing for the vendor portal (read-only).
 * Maps to /api/vendor-portal/activity endpoint
 */
export class VendorPortalActivityService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalActivityService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalActivityService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== LOGS ====================
    /**
     * Get all activity logs for the current vendor
     * GET /api/vendor-portal/activity/logs
     * Returns enriched log entries with user and action details
     */
    async getLogs() {
        const response = await this.getLogsRaw();
        return response.data ?? [];
    }
    /**
     * Get all activity logs (raw response)
     */
    getLogsRaw() {
        return this.get('logs');
    }
    // ==================== STATUS HISTORY ====================
    /**
     * Get vendor status change history for the current vendor
     * GET /api/vendor-portal/activity/status-history
     * Shows the timeline of status transitions (Draft → Submitted → Approved, etc.)
     */
    async getStatusHistory() {
        const response = await this.getStatusHistoryRaw();
        return response.data ?? [];
    }
    /**
     * Get status change history (raw response)
     */
    getStatusHistoryRaw() {
        return this.get('status-history');
    }
    // ==================== COMBINED TIMELINE ====================
    /**
     * Get combined activity timeline merging logs and status history
     * GET /api/vendor-portal/activity/combined
     * Returns a unified timeline sorted by timestamp (most recent first)
     */
    async getCombinedActivity() {
        const response = await this.getCombinedActivityRaw();
        return response.data ?? [];
    }
    /**
     * Get combined activity timeline (raw response)
     */
    getCombinedActivityRaw() {
        return this.get('combined');
    }
    // ==================== SUMMARY ====================
    /**
     * Get activity summary statistics for the current vendor
     * GET /api/vendor-portal/activity/summary
     * Provides counts and date ranges for activity logs and status changes
     */
    async getSummary() {
        const response = await this.getSummaryRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve activity summary');
        }
        return response.data;
    }
    /**
     * Get activity summary (raw response)
     */
    getSummaryRaw() {
        return this.get('summary');
    }
}
VendorPortalActivityService.DEFAULT_SERVICE_PATH = 'vendors';
VendorPortalActivityService.BASE_PATH = 'api/vendor-portal/activity';
//# sourceMappingURL=VendorPortalActivityService.js.map